import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { Mail, Phone, Calendar, User, MessageSquare, Trash2, Download } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMessages = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setMessages(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    toast({
      title: 'Message Deleted',
      description: 'The message has been removed.',
    });
    
    setMessages(messages.filter(msg => msg.id !== id));
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Message', 'Date'];
    const rows = messages.map(msg => [
      msg.name,
      msg.email,
      msg.phone,
      msg.message.replace(/,/g, ';'),
      new Date(msg.timestamp).toLocaleString()
    ]);

    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `messages_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();

    toast({
      title: 'Export Successful',
      description: 'Messages exported to CSV file.',
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredMessages = filter === 'today' 
    ? messages.filter(msg => new Date(msg.timestamp).toDateString() === new Date().toDateString())
    : messages;

  return (
    <AdminLayout>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Contact Messages</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600"
            >
              <option value="all">All Messages</option>
              <option value="today">Today</option>
            </select>
            <button
              onClick={exportToCSV}
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Total: <span className="font-semibold text-gray-900">{filteredMessages.length}</span> messages
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading messages...</p>
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No messages yet</h3>
              <p className="text-gray-600">Contact form submissions will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMessages.map((message, index) => (
                <div
                  key={message.id || index}
                  className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-500">Name</p>
                          <p className="font-semibold text-gray-900 truncate">{message.name}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-500">Email</p>
                          <a
                            href={`mailto:${message.email}`}
                            className="font-semibold text-blue-600 hover:text-blue-700 truncate block"
                          >
                            {message.email}
                          </a>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Phone className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-500">Phone</p>
                          <a
                            href={`tel:${message.phone}`}
                            className="font-semibold text-blue-600 hover:text-blue-700"
                          >
                            {message.phone}
                          </a>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Submitted</p>
                          <p className="font-semibold text-gray-900">
                            {formatDate(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div>
                      <div className="bg-gray-50 rounded-lg p-4 h-full flex flex-col">
                        <p className="text-sm text-gray-500 mb-2">Message</p>
                        <p className="text-gray-900 leading-relaxed whitespace-pre-wrap flex-1">
                          {message.message}
                        </p>
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => handleDelete(message.id)}
                            className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Message
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
